import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { AppInsightAddToCart } from 'entities/app-insight-add-to-cart';
import { MscPayer, PayerSearchResult } from 'entities/customer-search';
import { VerifiedPrice } from 'entities/parts/verified-price';
import { MscPriceVerifyData } from 'entities/price-verify/msc-price-verify-data';
import { MscPriceVerifyPart } from 'entities/price-verify/msc-price-verify-part';
import { ResultMessage } from 'entities/result-message';
import { ToastType } from 'entities/toast-type';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CommonDataService } from 'services/common-data.service';
import { LoaderService } from 'services/loader.service';
import { LoggerService, SourceLocationType } from 'services/logger.service';
import { PartService } from 'services/part.service';
import { ToastService } from 'services/toast.service';
import { AppState } from 'store/app-state';
import * as CartActions from 'store/cart/cart.actions';
import * as CustomerActions from 'store/customer/customer.actions';
import * as CustomerSelectors from 'store/customer/customer.selectors';
import * as VerifiedPricesActions from 'store/verified-prices/verified-prices.actions';

@Component({
    selector: 'price-verify-modal',
    templateUrl: './price-verify-modal.component.html',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule
    ]
})
export class PriceVerifyModalComponent implements OnInit {
  public loading: boolean = true;
  public payer$ = signal<MscPayer | null>(null);
  public payers$ = signal<PayerSearchResult | null>(null);
  private readonly destroyRef = inject(DestroyRef);
  public selectedRewardNumber$: Observable<string>;
  private originalPayerNumber: string;
  private originalPayerCardNumber: string;


  @Input() source: string;
  @Input() mscPriceVerifyParts: MscPriceVerifyPart[];
  @Input() parts: any;
  @Output() mscPriceVerifiedOut: EventEmitter<any> = new EventEmitter();
  @Input() cartId;

  constructor(private readonly loader: LoaderService,
    private readonly toastService: ToastService,
    public commonDataService: CommonDataService,
    private readonly partService: PartService,
    private readonly loggerService: LoggerService,
    private readonly activeModal: NgbActiveModal,
    private readonly store: Store<AppState>
  ) { }

  get payerCardNumber(): string {
    return this.payer$()?.payerCardNumber || '';
  }

  set payerCardNumber(value: string) {
    this.payer$.set({
      ...this.payer$(),
      payerCardNumber: value
    } as MscPayer);
  }

  setOriginalPayerInfo(payerNumber?: string, payerCardNumber?: string): void {
    if (payerNumber && !this.originalPayerNumber) {
      this.originalPayerNumber = payerNumber;
    }
    if (payerCardNumber && !this.originalPayerCardNumber) {
      this.originalPayerCardNumber = payerCardNumber;
    }
  }

  ngOnInit(): void {
    this.selectedRewardNumber$ = this.store.select(CustomerSelectors.selectedCustomerOrCustomerWithMscPayer)
      .pipe(
        tap((customer) => {
          if (!this.payer$() || !this.payer$()?.payerNumber) {
            this.payer$.set({
              ...this.payer$(),
              payerNumber: customer.addressNumber
            } as MscPayer);
          }
        }),
        map(x => x.rewardNumber)
      )
      .pipe(takeUntilDestroyed(this.destroyRef));

    this.store.select(CustomerSelectors.selectVerificationPayers)
      .pipe(
        tap((result) => {
          if (!this.payer$() || !this.payer$()?.payerCardNumber) {
            this.payer$.set({
              ...this.payer$(),
              payerCardNumber: result?.hinoCardNumber || result?.ppfsOrFcCardNumber || ''
            } as MscPayer);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => this.payers$.set(result));

    this.store.select(CustomerSelectors.selectMscPayer)
      .pipe(
        tap((payer) => {
          this.loading = false;
          if (payer !== null) {
            this.payer$.set({ ...payer });
          }
          this.setOriginalPayerInfo(this.payer$()?.payerNumber, this.payer$()?.payerCardNumber);
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  // TODO: [task="remove this method (and potentially many/all other changes from PR !29754 & !29809)"][reason="this is a temporary fix until such time as we can update the price verify logic to always show the user the modal that includes the payer type radio buttons"][id="202507122132"]
  getPayerNumberFromCardNumber(payerCardNumber: string): string {
    if (!payerCardNumber || (payerCardNumber.length !== 8 && payerCardNumber.length !== 16)) {
      return this.payer$()?.payerNumber || '';
    }

    let ppfsPayerNumber: string = "";
    let hinoPayerNumber: string = "";
    let fcPayerNumber: string = "";

    this.payers$().payers.forEach(p => {
      if (p.name.startsWith("PPFS")) {
        ppfsPayerNumber = p.payerNumber;
      } else if (p.name.startsWith("HINO")) {
        hinoPayerNumber = p.payerNumber;
      } else {
        fcPayerNumber = p.payerNumber;
      }
    });

    if (ppfsPayerNumber && payerCardNumber.length === 16 && payerCardNumber.startsWith('629856')) { // PPFS
      return ppfsPayerNumber;
    } else if (fcPayerNumber && payerCardNumber.length === 16 && payerCardNumber.startsWith('622086')) { // FC
      return fcPayerNumber;
    } else if (hinoPayerNumber && payerCardNumber.length === 16 && payerCardNumber.startsWith('700000')) { // Hino
      return hinoPayerNumber;
    } else if (fcPayerNumber && payerCardNumber.length === 8) { // FC
      return fcPayerNumber;
    }

    // Default case
    return this.payer$()?.payerNumber || '';
  }

  buildMscPriceVerifyData(rewardNumber: string): MscPriceVerifyData {
    let result: MscPriceVerifyData =
    {
      mscPriceVerifyParts: this.mscPriceVerifyParts,
      payerAccountNumber: this.getPayerNumberFromCardNumber(this.payer$()?.payerCardNumber || ''),
      mscCardNumber: this.payer$()?.payerCardNumber || '',
      branchCode: this.commonDataService.Branch.code,
      cartId: this.cartId,
      salesOrganization: null,
      rewardNumber
    };

    return result;
  }

  dispatchPayerUpdate(mscPriceVerifyData: MscPriceVerifyData): void {
    if (mscPriceVerifyData?.payerAccountNumber &&
      mscPriceVerifyData?.mscCardNumber &&
      (mscPriceVerifyData?.payerAccountNumber !== this.originalPayerNumber) &&
      (mscPriceVerifyData?.mscCardNumber !== this.originalPayerCardNumber)) {
      this.store.dispatch(CustomerActions.updateMscPayerAfterCardNumberChange({
        payerNumber: mscPriceVerifyData?.payerAccountNumber,
        payerCardNumber: mscPriceVerifyData?.mscCardNumber
      }));
      this.store.dispatch(CustomerActions.updateCheckoutWithSelectedPayer());
    }
  }

  priceVerification(rewardNumber: string) {
    let mscPriceVerifyData = this.buildMscPriceVerifyData(rewardNumber);
    this.loader.loading = true;
    this.dispatchPayerUpdate(mscPriceVerifyData);
    this.partService.getPriceVerification(mscPriceVerifyData)
      .then(p => {
        this.loader.loading = false;
        if(p.priceVerifyItems.some(x => !x.isPriceVerified) && p.messages.length === 0){
          this.toastService.showToast('Unable to price-verify some parts', ToastType.Info);
        }
        if (p.ErrorType != undefined && p.ErrorType != null && p.ErrorType != 200) {
          this.toastService.errorMessage('PriceVerifyModalComponent', 'priceVerification', 'getPriceVerification', p);
          this.store.dispatch(CartActions.priceVerifyFailed({ error: p }));
        } else {
          if (p.priceVerifyItems != null && p.priceVerifyItems != undefined && p.priceVerifyItems.length > 0) {
            // Use the first item for backwards compatibility, but we'll process all items below
            const mscPriceVerifiedData = {
              isPriceVerified: p.priceVerifyItems[0].isPriceVerified,
              itemCategory: p.priceVerifyItems[0].itemCategory,
              partNumber: p.priceVerifyItems[0].partNumber,
              quantity: p.priceVerifyItems[0].quantity,
              rebate: p.priceVerifyItems[0].rebate,
              unitPrice: p.priceVerifyItems[0].unitPrice,
              coupons: p.priceVerifyItems[0].coupons
            };

            const isPriceVerified = p.priceVerifyItems
              .filter((priceVerifyItem) => priceVerifyItem.isPriceVerified);
            if (isPriceVerified.length > 0) {
              let appInsightAddToCart = Object.assign(new AppInsightAddToCart(), {
                userId: this.commonDataService.User.id,
                rewardNumber: this.commonDataService.Customer.rewardNumber,
                customerName: this.commonDataService.Customer.customerName,
                branchNumber: this.commonDataService.Branch.code,
                cartDetails: JSON.stringify(this.commonDataService.CartDetails),
                source: SourceLocationType[this.source]
              });
              appInsightAddToCart.product = this.loggerService.getAppInsightParts(this.parts, JSON.stringify(appInsightAddToCart).length);
              this.loggerService.trackMetric('PriceVerifyComponent_priceVerify_mscPriceVerification', appInsightAddToCart);
            }
            this.mscPriceVerifiedOut.emit(mscPriceVerifiedData);
            this.showResponseMessage(isPriceVerified, p.messages);

            if(p.cartUpdatedWithVerifiedPrices == null){
              // Process all verified items, not just the first one
              p.priceVerifyItems.forEach(verifiedItem => {
                if(verifiedItem.isPriceVerified){
                  const verifiedItemCoupons = verifiedItem.coupons || [];
                  const mscPriceVerifyPartCoupons =
                    this.mscPriceVerifyParts.find(
                      (p) => p.partNumber === verifiedItem.partNumber,
                    )?.sapCoupons || [];
                  const allCoupons = [
                    ...verifiedItemCoupons,
                    ...mscPriceVerifyPartCoupons,
                  ];
                  const verifiedPrice: VerifiedPrice = {
                    partNumber: verifiedItem?.partNumber,
                    rebate: verifiedItem?.rebate,
                    unitPrice: verifiedItem?.unitPrice,
                    coupons: allCoupons,
                  };
                  this.store.dispatch(VerifiedPricesActions.upsertVerifiedPrice({verifiedPrice}));
                }
              });
            } else {
              this.store.dispatch(CartActions.priceVerifySuccess({ cart: p.cartUpdatedWithVerifiedPrices }));
            }

            this.activeModal.close({ cartId: mscPriceVerifyData.cartId });
          }
        }
        this.loggerService.verbose('PriceVerify Component: ', p);

      },
        () => { this.loader.loading = false; });
  }

  showResponseMessage(isPriceVerified: any[], messages: ResultMessage[]) {
    const successMessageObject = { message: 'The Price has been verified and adjusted.', type: ToastType.Success };

    if (isPriceVerified && isPriceVerified.length > 0 && messages.length <= 0) {
      this.toastService.showToast(successMessageObject.message, successMessageObject.type);
    } else if (isPriceVerified && isPriceVerified.length > 0 && messages.length > 0) {
      const toastMessages = messages
        .map((message) => this.toastService.toToastMessage(message))
        .concat(successMessageObject);
      this.toastService.showMultilineToast(toastMessages);
    } else if (messages.length > 1) {
      const toastMessages = messages
        .map((message) => this.toastService.toToastMessage(message));
      this.toastService.showMultilineToast(toastMessages);
    } else if (messages.length == 1) {
      this.toastService.showToast(messages[0].message, this.toastService.toToastType(messages[0].level));
    }
  }
}
